import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  bpsToPercent,
  getSuperAdminCoupons,
  SaCoupon,
} from "../../../helpers/super_admin_coupons_helper";
import { intlLocaleFromI18n } from "../../../helpers/money";
import CouponFormModal from "./CouponFormModal";
import CouponApplicationsModal from "./CouponApplicationsModal";

const PER_PAGE = 50;

type ActiveFilter = "" | "1" | "0";

/**
 * Listagem + CRUD de cupons SA (DOC 02 § 4 · etapa 2 UI).
 * % na tabela/modal; API recebe discount_percent_bps.
 */
const SuperAdminCouponsList = () => {
  const { t, i18n } = useTranslation();
  const [coupons, setCoupons] = useState<SaCoupon[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<SaCoupon | null>(null);
  const [applicationsCoupon, setApplicationsCoupon] = useState<SaCoupon | null>(
    null
  );

  const showInitialLoading = loading && coupons.length === 0 && !error;
  const showRefreshOverlay = loading && coupons.length > 0;
  const hasFilters = activeFilter !== "" || search !== "";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSuperAdminCoupons({
        per_page: PER_PAGE,
        page,
        search: search || undefined,
        is_active: activeFilter === "" ? undefined : activeFilter === "1",
      });
      if (!res?.success) {
        setError(t("sa.coupons.error.load"));
        setCoupons([]);
        setTotal(0);
        setLastPage(1);
        return;
      }
      setCoupons(res.data || []);
      setTotal(res.meta?.total ?? (res.data || []).length);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (err: any) {
      setError(err?.message || t("sa.coupons.error.load"));
      setCoupons([]);
      setTotal(0);
      setLastPage(1);
    } finally {
      setLoading(false);
    }
  }, [t, page, search, activeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  document.title = t("sa.coupons.documentTitle");

  const openCreate = () => {
    setEditingCoupon(null);
    setModalOpen(true);
  };

  const openEdit = (coupon: SaCoupon) => {
    setEditingCoupon(coupon);
    setModalOpen(true);
  };

  const formatPercent = (bps: number) =>
    t("sa.coupons.percentValue", {
      value: new Intl.NumberFormat(intlLocaleFromI18n(i18n.language), {
        maximumFractionDigits: 2,
      }).format(bpsToPercent(bps)),
    });

  const formatRedemptions = (coupon: SaCoupon) => {
    const used = new Intl.NumberFormat(
      intlLocaleFromI18n(i18n.language)
    ).format(coupon.redemption_count ?? 0);
    if (coupon.max_redemptions == null) {
      return t("sa.coupons.redemptionsUnlimited", { used });
    }
    const max = new Intl.NumberFormat(
      intlLocaleFromI18n(i18n.language)
    ).format(coupon.max_redemptions);
    return t("sa.coupons.redemptionsLimited", { used, max });
  };

  const formatValidity = (coupon: SaCoupon) => {
    const locale = intlLocaleFromI18n(i18n.language);
    const fmt = (iso: string | null) => {
      if (!iso) return null;
      try {
        return new Intl.DateTimeFormat(locale, {
          dateStyle: "short",
          timeZone: "UTC",
        }).format(new Date(iso));
      } catch {
        return iso.slice(0, 10);
      }
    };
    const from = fmt(coupon.valid_from);
    const until = fmt(coupon.valid_until);
    if (!from && !until) {
      return t("sa.coupons.validityOpen");
    }
    if (from && until) {
      return t("sa.coupons.validityRange", { from, until });
    }
    if (from) {
      return t("sa.coupons.validityFrom", { from });
    }
    return t("sa.coupons.validityUntil", { until: until as string });
  };

  const statusBadge = (active: boolean) => (
    <Badge color={active ? "success" : "secondary"}>
      {active ? t("sa.coupons.status.active") : t("sa.coupons.status.inactive")}
    </Badge>
  );

  /** Espelha valid_until < now UTC na API (CouponRedemptionService). */
  const isCouponExpired = (coupon: SaCoupon): boolean => {
    if (!coupon.valid_until) return false;
    return new Date(coupon.valid_until).getTime() < Date.now();
  };

  const isCouponExhausted = (coupon: SaCoupon): boolean =>
    coupon.max_redemptions != null &&
    (coupon.redemption_count ?? 0) >= coupon.max_redemptions;

  const copyCouponCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("sa.coupons.toast.codeCopied"), { autoClose: 2000 });
    } catch {
      toast.error(t("sa.coupons.toast.copyFailed"), { autoClose: 2500 });
    }
  };

  const renderStatusCell = (coupon: SaCoupon) => (
    <div className="d-flex flex-wrap gap-1 align-items-center">
      {statusBadge(coupon.is_active)}
      {isCouponExhausted(coupon) ? (
        <Badge color="warning">{t("sa.coupons.badge.exhausted")}</Badge>
      ) : null}
      {isCouponExpired(coupon) ? (
        <Badge color="danger">{t("sa.coupons.badge.expired")}</Badge>
      ) : null}
    </div>
  );

  const applySearch = () => {
    setPage(1);
    setSearch(searchDraft.trim());
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.coupons.title")}
          pageTitle={t("sa.menu.billing")}
        />
        <ToastContainer closeButton={false} limit={2} />

        {error ? (
          <Alert
            color="danger"
            className="d-flex align-items-center justify-content-between gap-2"
          >
            <span>{error}</span>
            <Button
              color="light"
              size="sm"
              onClick={() => load()}
              disabled={loading}
            >
              {t("sa.coupons.retry")}
            </Button>
          </Alert>
        ) : null}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <h5 className="card-title mb-0">
                    {t("sa.coupons.tableTitle")}
                  </h5>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {!loading && !error ? (
                      <span className="text-muted small">
                        {t("sa.coupons.totalCount", { count: total })}
                      </span>
                    ) : null}
                    <Button
                      color="primary"
                      size="sm"
                      onClick={openCreate}
                      disabled={loading}
                    >
                      <i
                        className="ri-add-line align-bottom me-1"
                        aria-hidden
                      />
                      {t("sa.coupons.create")}
                    </Button>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <InputGroup style={{ maxWidth: 280 }}>
                    <Input
                      bsSize="sm"
                      value={searchDraft}
                      onChange={(e) => setSearchDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applySearch();
                        }
                      }}
                      placeholder={t("sa.coupons.filter.searchPlaceholder")}
                      aria-label={t("sa.coupons.filter.search")}
                      disabled={loading}
                    />
                    <InputGroupText
                      tag="button"
                      type="button"
                      className="btn btn-light btn-sm"
                      onClick={applySearch}
                      disabled={loading}
                    >
                      <i className="ri-search-line" aria-hidden />
                      <span className="visually-hidden">
                        {t("sa.coupons.filter.search")}
                      </span>
                    </InputGroupText>
                  </InputGroup>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ maxWidth: 200 }}
                    value={activeFilter}
                    onChange={(e) => {
                      setPage(1);
                      setActiveFilter(e.target.value as ActiveFilter);
                    }}
                    aria-label={t("sa.coupons.filter.status")}
                    disabled={loading}
                  >
                    <option value="">{t("sa.coupons.filter.statusAll")}</option>
                    <option value="1">{t("sa.coupons.status.active")}</option>
                    <option value="0">{t("sa.coupons.status.inactive")}</option>
                  </Input>
                </div>
              </CardHeader>
              <CardBody className="position-relative">
                {showRefreshOverlay ? (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                    style={{ zIndex: 2 }}
                    role="status"
                    aria-live="polite"
                  >
                    <Spinner color="primary" className="me-2" />
                    <span className="text-muted">{t("sa.coupons.loading")}</span>
                  </div>
                ) : null}

                {showInitialLoading ? (
                  <div className="text-center py-5" role="status">
                    <Spinner color="primary" className="me-2" />
                    <span className="text-muted">{t("sa.coupons.loading")}</span>
                  </div>
                ) : coupons.length === 0 && !error ? (
                  <div className="text-center py-5 px-3">
                    <div className="avatar-md mx-auto mb-3">
                      <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-24">
                        <i className="ri-coupon-3-line" aria-hidden="true" />
                      </div>
                    </div>
                    <h5 className="mb-2">
                      {hasFilters
                        ? t("sa.coupons.empty.filteredTitle")
                        : t("sa.coupons.empty.title")}
                    </h5>
                    <p
                      className="text-muted mb-3 mx-auto"
                      style={{ maxWidth: 420 }}
                    >
                      {hasFilters
                        ? t("sa.coupons.empty.filteredDescription")
                        : t("sa.coupons.empty.description")}
                    </p>
                    {hasFilters ? (
                      <Button
                        color="light"
                        onClick={() => {
                          setSearch("");
                          setSearchDraft("");
                          setActiveFilter("");
                          setPage(1);
                        }}
                      >
                        {t("sa.coupons.filter.clear")}
                      </Button>
                    ) : (
                      <Button color="primary" onClick={openCreate}>
                        {t("sa.coupons.create")}
                      </Button>
                    )}
                  </div>
                ) : coupons.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">{t("sa.coupons.table.code")}</th>
                            <th scope="col">{t("sa.coupons.table.discount")}</th>
                            <th scope="col">{t("sa.coupons.table.duration")}</th>
                            <th scope="col">
                              {t("sa.coupons.table.redemptions")}
                            </th>
                            <th scope="col">{t("sa.coupons.table.validity")}</th>
                            <th scope="col">{t("sa.coupons.table.status")}</th>
                            <th scope="col" className="text-end">
                              {t("sa.coupons.table.actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {coupons.map((coupon) => (
                            <tr key={coupon.id}>
                              <td className="fw-medium font-monospace">
                                <div className="d-flex align-items-center gap-2">
                                  <span>{coupon.code}</span>
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 text-muted"
                                    title={t("sa.coupons.copyCode")}
                                    aria-label={t("sa.coupons.copyCode")}
                                    onClick={() => void copyCouponCode(coupon.code)}
                                    disabled={loading}
                                  >
                                    <i className="ri-file-copy-line" aria-hidden />
                                  </Button>
                                </div>
                              </td>
                              <td className="text-nowrap">
                                {formatPercent(coupon.discount_percent_bps)}
                              </td>
                              <td>
                                <div>
                                  {t(
                                    `sa.coupons.duration.${coupon.duration_type}`,
                                    {
                                      defaultValue: coupon.duration_type,
                                    }
                                  )}
                                </div>
                                {coupon.duration_type === "limited_cycles" &&
                                coupon.duration_cycles != null ? (
                                  <div className="text-muted small">
                                    {t("sa.coupons.cyclesLabel", {
                                      count: coupon.duration_cycles,
                                    })}
                                  </div>
                                ) : null}
                              </td>
                              <td className="text-nowrap">
                                {formatRedemptions(coupon)}
                              </td>
                              <td className="text-nowrap">
                                {formatValidity(coupon)}
                              </td>
                              <td>{renderStatusCell(coupon)}</td>
                              <td className="text-end text-nowrap">
                                <Button
                                  color="light"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => setApplicationsCoupon(coupon)}
                                  disabled={loading}
                                >
                                  {t("sa.coupons.viewApplications")}
                                </Button>
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() => openEdit(coupon)}
                                  disabled={loading}
                                >
                                  {t("sa.coupons.edit")}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {lastPage > 1 ? (
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                        <span className="text-muted small">
                          {t("sa.coupons.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("sa.coupons.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage || loading}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("sa.coupons.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <CouponFormModal
        isOpen={modalOpen}
        coupon={editingCoupon}
        onClose={() => {
          setModalOpen(false);
          setEditingCoupon(null);
        }}
        onSaved={() => load()}
      />

      <CouponApplicationsModal
        isOpen={applicationsCoupon !== null}
        coupon={applicationsCoupon}
        onClose={() => setApplicationsCoupon(null)}
      />
    </div>
  );
};

export default SuperAdminCouponsList;
